import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ChallengeActivityQuery,
  ChallengeActivityQuestionAttemptHttpModel,
  ChallengeActivityQuestionAttemptQuery,
  ChallengeActivityQuestionAttemptService,
  ChallengeActivityQuestionAttemptStore,
  createChallengeActivityQuestionAttemptCompositeId,
} from '@examdojo/challenge';
import { RealtimeChangesListenEvent, RealtimeService } from '@examdojo/supabase';
import { concatMap, filter, map, merge, of, switchMap, withLatestFrom } from 'rxjs';
import { ExamdojoPlatformService } from '../../../shared/platform.service';

@Injectable()
export class ChallengeActivityQuestionAttemptsRealtimeService {
  constructor(
    private readonly realtimeService: RealtimeService,
    private readonly challengeActivityQuery: ChallengeActivityQuery,
    private readonly challengeActivityQuestionAttemptQuery: ChallengeActivityQuestionAttemptQuery,
    private readonly challengeActivityQuestionAttemptService: ChallengeActivityQuestionAttemptService,
    private readonly challengeActivityQuestionAttemptStore: ChallengeActivityQuestionAttemptStore,
    private readonly platformService: ExamdojoPlatformService,
  ) {
    merge(this.listenForChallengeActivityQuestionAttemptChanges()).pipe(takeUntilDestroyed()).subscribe();
  }

  private listenForChallengeActivityQuestionAttemptChanges() {
    return this.challengeActivityQuery.inProgressChallengeActivityId$.pipe(
      switchMap((challengeActivityId) => {
        if (!challengeActivityId) {
          return of(null);
        }

        return this.realtimeService
          .listenBroadcastRealtime<ChallengeActivityQuestionAttemptHttpModel>(
            [{ event: RealtimeChangesListenEvent.INSERT }, { event: RealtimeChangesListenEvent.UPDATE }],
            `challenge_activity_question_attempts:${challengeActivityId}`,
          )
          .pipe(
            withLatestFrom(this.platformService.activeStatusState$),
            filter(([_, isActive]) => isActive),
            map(([event]) => event),
            concatMap(({ event, payload }) => {
              const record = payload?.record;
              if (!record) {
                return of(null);
              }

              if (
                event === RealtimeChangesListenEvent.INSERT &&
                record.challenge_activity_id &&
                record.question_attempt_id &&
                !this.challengeActivityQuestionAttemptStore.hasEntity(
                  createChallengeActivityQuestionAttemptCompositeId(record.challenge_activity_id, record.question_id),
                )
              ) {
                this.challengeActivityQuestionAttemptService.upsertHttpModel(record);
              }

              const existingAttempt = this.challengeActivityQuestionAttemptQuery.getEntity(
                createChallengeActivityQuestionAttemptCompositeId(record.challenge_activity_id, record.question_id),
              );

              if (
                event === RealtimeChangesListenEvent.UPDATE &&
                record.question_attempt_id &&
                record.challenge_activity_id &&
                existingAttempt?.status !== record.status
              ) {
                this.challengeActivityQuestionAttemptService.upsertHttpModel(record);
              }

              return of(null);
            }),
          );
      }),
    );
  }
}
